import { AdvancedMarker, ColorScheme, Map as GoogleMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { useEffect, useMemo, useState } from 'react';
import { Area, AreaChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { StatRow, StatTile } from '@/components/admin/StatTile';
import { Button, Icon, Segmented, TextInput } from '@/components/ds';
import { lineSeries, quietAxis, quietGrid } from '@/components/viz';
import { useAdminAcquisitionInsights } from '@/hooks/admin/useAdminAcquisitionInsights';
import {
  AdminAcquisitionInsightsCompare,
  AdminAcquisitionInsightsGeoRow,
  AdminAcquisitionInsightsPeriod,
  AdminAcquisitionInsightsRankedRow,
} from '@/services/api/admin';
import { GOOGLE_MAPS_MAP_ID } from '@/types/maps';

const C = {
  bg: 'var(--tx-fill-2)',
  card: 'var(--tx-glass-panel)',
  surface: 'var(--tx-fill)',
  border: 'var(--tx-line)',
  text: 'var(--tx-text)',
  muted: 'var(--tx-muted)',
  subtle: 'var(--tx-faint)',
  lime: '#c0ff02',
  // Hauser data palette: up reads as resolved (lime), down as urgent, flat/unknown as monitor
  urgent: '#FF9A4D',
  monitor: '#9A938A',
  // the viz system's neutral bar ink (Bars' default)
  bar: 'rgba(var(--tx-ink-rgb), 0.28)',
};
const font = "'Manrope', sans-serif";

const PLANNED_SOURCES = [
  'Google Analytics 4',
  'Meta Ads',
  'Google Ads',
  'Hauser.com UTM',
  'Organic / Direct',
];

const PERIOD_OPTIONS: Array<{ label: string; value: AdminAcquisitionInsightsPeriod }> = [
  { label: '7D', value: 'last_7' },
  { label: '30D', value: 'last_30' },
  { label: '90D', value: 'last_90' },
  { label: 'MTD', value: 'mtd' },
  { label: 'Prev month', value: 'previous_month' },
  { label: 'YTD', value: 'ytd' },
  { label: 'Custom', value: 'custom' },
];

const COMPARE_OPTIONS: Array<{ label: string; value: AdminAcquisitionInsightsCompare }> = [
  { label: 'Previous period', value: 'previous_period' },
  { label: 'Previous month', value: 'previous_month' },
  { label: 'Previous year', value: 'previous_year' },
];

const GEO_MAP_BACKGROUND = 'linear-gradient(145deg, rgba(255,255,255,0.035), rgba(255,255,255,0.01)), #222';

// the date wells sit beside the Compare control at the same 40px height; the native picker glyph stays muted
const dateInputSx = {
  width: 160,
  fontFamily: font,
  '& input': { colorScheme: 'light dark' },
  '& input::-webkit-calendar-picker-indicator': { opacity: 0.55, cursor: 'pointer' },
} as const;

// tooltip: frosted glass, radius 12, no border (the app's hover-card treatment)
const tooltipStyle: React.CSSProperties = {
  background: 'var(--tx-glass)',
  backdropFilter: 'var(--tx-blur)',
  WebkitBackdropFilter: 'var(--tx-blur)',
  borderRadius: 12,
  color: C.text,
  fontFamily: font,
  fontSize: 12,
  padding: '10px 12px',
  boxShadow: 'var(--tx-shadow-sm)',
};

const fallback = {
  generatedAt: '',
  ga4: {
    propertyId: '472617569',
    period: {
      period: 'last_30' as AdminAcquisitionInsightsPeriod,
      compare: 'previous_period' as AdminAcquisitionInsightsCompare,
      current: {
        startDate: '',
        endDate: '',
      },
      previous: {
        startDate: '',
        endDate: '',
      },
    },
    current: {
      activeUsers: 0,
      newUsers: 0,
      sessions: 0,
      views: 0,
      engagedSessions: 0,
      eventCount: 0,
      engagementSeconds: 0,
    },
    previous: {
      activeUsers: 0,
      newUsers: 0,
      sessions: 0,
      views: 0,
      engagedSessions: 0,
      eventCount: 0,
      engagementSeconds: 0,
    },
    deltas: {
      activeUsers: null,
      newUsers: null,
      sessions: null,
      views: null,
      engagedSessions: null,
      eventCount: null,
      engagementSeconds: null,
    },
    daily: [],
    deviceEngagement: [],
    topSources: [],
    topPages: [],
    topEvents: [],
    topLocations: [],
  },
  rawAvailability: {
    ga4: false,
  },
  errors: [],
};

const number = (value: number) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);

const compact = (value: number) => new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value);

const decimal = (value: number) => new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
}).format(value);

const pct = (value: number | null) => {
  if (value === null || !Number.isFinite(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${(value * 100).toFixed(0)}%`;
};

const relativeDelta = (current: number, previous: number) => {
  if (!previous) return current ? 1 : null;
  return (current - previous) / previous;
};

const formatSeconds = (seconds: number) => {
  if (!seconds || !Number.isFinite(seconds)) return '0s';
  const rounded = Math.round(seconds);
  if (rounded < 60) return `${rounded}s`;
  const minutes = Math.floor(rounded / 60);
  const remainder = rounded % 60;
  return remainder ? `${minutes}m ${remainder}s` : `${minutes}m`;
};

const formatDate = (date: string) => {
  const parsed = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

const formatFullDate = (date: string) => {
  const parsed = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const localIsoDate = (date: Date) => date.toISOString().slice(0, 10);

const defaultEndDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return localIsoDate(date);
};

const defaultStartDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return localIsoDate(date);
};

const compareLabel = (mode: AdminAcquisitionInsightsCompare) =>
  COMPARE_OPTIONS.find((option) => option.value === mode)?.label.toLowerCase() ?? 'comparison period';

const compareDisplayLabel = (mode: AdminAcquisitionInsightsCompare) =>
  COMPARE_OPTIONS.find((option) => option.value === mode)?.label ?? 'Previous period';

const periodTrendLabel = (period: AdminAcquisitionInsightsPeriod) => {
  switch (period) {
    case 'last_7':
      return '7-day trend';
    case 'last_30':
      return '30-day trend';
    case 'last_90':
      return '90-day trend';
    case 'mtd':
      return 'Month-to-date trend';
    case 'previous_month':
      return 'Previous month trend';
    case 'ytd':
      return 'Year-to-date trend';
    case 'custom':
      return 'Custom trend';
    default:
      return 'Traffic trend';
  }
};

const Card: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <div
    style={{
      background: C.card,
      backgroundImage: 'var(--tx-surface-light)',
      boxShadow: 'var(--tx-shadow)',
      borderRadius: '24px',
      padding: 20,
      ...style,
    }}>
    {children}
  </div>
);

// a section title inside a sheet: 17/400 ink, sentence case
const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ fontSize: 17, fontWeight: 400, letterSpacing: '-0.015em', color: C.text, lineHeight: 1.25 }}>
    {children}
  </div>
);

// the meta caption on a title's line: 11/500 muted
const metaStyle: React.CSSProperties = { fontSize: 11, fontWeight: 500, color: C.muted };

// a title with its meta caption on the right
const SectionHeader: React.FC<{ title: React.ReactNode; meta?: React.ReactNode; style?: React.CSSProperties }> = ({
  title,
  meta,
  style,
}) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      flexWrap: 'wrap',
      columnGap: 16,
      rowGap: 4,
      ...style,
    }}>
    <SectionTitle>{title}</SectionTitle>
    {meta != null && <div style={metaStyle}>{meta}</div>}
  </div>
);

// the app's legend item: a 2px x 12px tick in the colour, then the label
const Tick: React.FC<{ color: string }> = ({ color }) => (
  <span aria-hidden="true" style={{ width: 2, height: 12, borderRadius: 1, background: color, flexShrink: 0 }} />
);

const GaIcon: React.FC<{ muted?: boolean }> = ({ muted }) => (
  <span
    aria-hidden="true"
    style={{
      width: 14,
      height: 14,
      display: 'inline-flex',
      alignItems: 'flex-end',
      gap: 2,
    }}>
    <span
      style={{
        width: 4,
        height: 4,
        borderRadius: '50%',
        background: muted ? C.subtle : '#F57C00',
        opacity: muted ? 0.45 : 1,
      }}
    />
    <span
      style={{
        width: 4,
        height: 9,
        borderRadius: 3,
        background: muted ? C.subtle : '#F57C00',
        opacity: muted ? 0.55 : 1,
      }}
    />
    <span
      style={{
        width: 4,
        height: 14,
        borderRadius: 3,
        background: muted ? C.subtle : '#F9AB00',
        opacity: muted ? 0.65 : 1,
      }}
    />
  </span>
);

// Stat tile (the app's StatTile: value first, label beneath). The visual is this period against the comparison
// period: the segment is this period's level (in the direction's colour), the small mark is where the comparison
// period stood on the same scale. Not a share of a whole, so it stays bespoke rather than a ShareStrip
const PeriodHairline: React.FC<{ current: number; previous: number; tone: string; comparisonLabel: string }> = ({
  current,
  previous,
  tone,
  comparisonLabel,
}) => {
  const scale = Math.max(current, previous, 0);
  const currentShare = scale ? Math.min(100, (Math.max(current, 0) / scale) * 100) : 0;
  const previousShare = scale ? Math.min(100, (Math.max(previous, 0) / scale) * 100) : 0;
  return (
    <div
      title={`This period vs ${comparisonLabel}`}
      style={{ position: 'relative', height: 4, borderRadius: 2, background: 'var(--tx-fill-2)' }}>
      {scale > 0 && (
        <>
          <span
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: `${currentShare}%`,
              background: tone,
              borderRadius: 2,
            }}
          />
          {previous > 0 && (
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: `calc(${previousShare}% - 1px)`,
                top: -2,
                width: 2,
                height: 8,
                borderRadius: 1,
                background: C.muted,
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

const MetricCard: React.FC<{
  label: string;
  value: string | number;
  current: number;
  previous: number;
  delta: number | null;
  comparisonLabel: string;
  loading?: boolean;
}> = ({ label, value, current, previous, delta, comparisonLabel, loading }) => {
  const tone = delta === null || !Number.isFinite(delta) || delta === 0 ? C.monitor : delta > 0 ? C.lime : C.urgent;
  return (
    <StatTile
      value={typeof value === 'number' ? number(value) : value}
      label={label}
      loading={loading}
      visual={<PeriodHairline current={current} previous={previous} tone={tone} comparisonLabel={comparisonLabel} />}
      caption={
        <>
          <span style={{ color: C.text, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{pct(delta)}</span> vs{' '}
          {comparisonLabel}
        </>
      }
    />
  );
};

const RankedRows: React.FC<{
  rows: AdminAcquisitionInsightsRankedRow[];
  valueLabel: string;
  secondaryLabel: string;
}> = ({ rows, valueLabel, secondaryLabel }) => {
  const max = rows[0]?.value || 1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {rows.map((row, i) => (
        <div
          key={`${row.label}-${i}`}
          style={{
            padding: '12px 0',
            borderBottom: i === rows.length - 1 ? 'none' : '1px solid var(--tx-line)',
          }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 18, alignItems: 'baseline' }}>
            <div
              title={row.label}
              style={{
                minWidth: 0,
                color: C.text,
                fontSize: 13,
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
              {row.label}
            </div>
            <div style={{ color: C.text, fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
              {number(row.value)}
            </div>
          </div>
          {/* the system's thin bar: 4px, 2px corners, neutral ink on the quiet track (a ranking of one series) */}
          <div style={{ marginTop: 8, height: 4, background: 'var(--tx-fill-2)', borderRadius: 2, overflow: 'hidden' }}>
            <div
              style={{
                width: `${Math.min(100, (row.value / max) * 100)}%`,
                height: '100%',
                background: C.bar,
                borderRadius: 2,
              }}
            />
          </div>
          <div style={{ color: C.subtle, fontSize: 11, fontWeight: 500, marginTop: 6 }}>
            {valueLabel} · {number(row.secondary ?? 0)} {secondaryLabel}
          </div>
        </div>
      ))}
    </div>
  );
};

const GeoMapCard: React.FC<{ rows: AdminAcquisitionInsightsGeoRow[] }> = ({ rows }) => {
  const geocodingLibrary = useMapsLibrary('geocoding');
  const usRows = useMemo(
    () => rows.filter((row) => row.country === 'United States' && row.region).slice(0, 10),
    [rows],
  );
  const [positions, setPositions] = useState<Record<string, google.maps.LatLngLiteral>>({});
  const max = Math.max(...usRows.map((row) => row.value), 1);

  useEffect(() => {
    if (!geocodingLibrary || !usRows.length) return;

    let cancelled = false;
    const geocoder = new geocodingLibrary.Geocoder();
    const missingRows = usRows.filter((row) => !positions[row.label]);

    if (!missingRows.length) return;

    Promise.all(
      missingRows.map(async (row) => {
        const address = `${row.city ? `${row.city}, ` : ''}${row.region}, United States`;
        try {
          const result = await geocoder.geocode({ address, region: 'US' });
          const location = result.results[0]?.geometry.location;

          if (!location) return null;

          return {
            label: row.label,
            position: {
              lat: location.lat(),
              lng: location.lng(),
            },
          };
        } catch {
          return null;
        }
      }),
    ).then((results) => {
      if (cancelled) return;

      setPositions((current) => {
        const next = { ...current };
        results.forEach((result) => {
          if (result) next[result.label] = result.position;
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [geocodingLibrary, positions, usRows]);

  return (
    // the map runs full bleed down the card's left half (its corners are the card's); the title and the city list
    // take the right half
    <Card style={{ padding: 0, overflow: 'hidden' }}>
      {usRows.length ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
            alignItems: 'stretch',
            minHeight: 590,
          }}>
          <div
            style={{
              position: 'relative',
              minHeight: 590,
              background: GEO_MAP_BACKGROUND,
              overflow: 'hidden',
            }}>
            {!import.meta.env.VITE_GOOGLE_MAPS_API_KEY ? (
              <EmptyState title="Map unavailable" detail="VITE_GOOGLE_MAPS_API_KEY is not configured." />
            ) : (
              <GoogleMap
                mapId={GOOGLE_MAPS_MAP_ID}
                defaultCenter={{ lat: 39.5, lng: -98.35 }}
                defaultZoom={4}
                gestureHandling="greedy"
                // Night follows the app into the map style's dark scheme; no on-map controls (Google's logo and
                // attribution stay, as its terms require)
                colorScheme={document.documentElement.dataset.mode === 'night' ? ColorScheme.DARK : ColorScheme.LIGHT}
                disableDefaultUI
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                {usRows.map((row, i) => {
                  const position = positions[row.label];
                  if (!position) return null;

                  const size = 14 + (row.value / max) * 30;

                  return (
                    <AdvancedMarker key={`${row.label}-${i}`} position={position}>
                      <div
                        title={`${row.label}: ${number(row.value)} active users`}
                        style={{
                          width: size,
                          height: size,
                          borderRadius: '50%',
                          background: i === 0 ? C.lime : C.muted,
                          border: `2px solid ${C.card}`,
                          boxShadow: `0 0 0 ${Math.max(5, size / 2)}px ${i === 0 ? 'rgba(192,255,2,0.12)' : 'rgba(187,183,175,0.08)'}`,
                        }}
                      />
                    </AdvancedMarker>
                  );
                })}
              </GoogleMap>
            )}
            {/* top-left, so it never covers the Google logo (bottom-left) or the attribution (bottom-right) */}
            <div
              style={{
                position: 'absolute',
                left: 14,
                top: 12,
                color: C.muted,
                fontSize: 11,
                fontWeight: 500,
                pointerEvents: 'none',
              }}>
              United States
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, padding: 20, maxHeight: 590, boxSizing: 'border-box' }}>
            <SectionHeader
              title="Visitor geography"
              meta="U.S. city/state traffic · bubble size = active users"
              style={{ marginBottom: 8 }}
            />
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 6, scrollbarWidth: 'none' }}>
            {usRows.map((row, i) => (
              <div
                key={`${row.label}-${i}`}
                style={{
                  padding: '11px 0',
                  borderBottom: i === usRows.length - 1 ? 'none' : '1px solid var(--tx-line)',
                }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'center' }}>
                  {/* the tick carries the marker's colour, so the top city reads back to its lime bubble */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <Tick color={i === 0 ? C.lime : C.muted} />
                    <div
                      title={row.label}
                      style={{
                        minWidth: 0,
                        color: C.text,
                        fontSize: 13,
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                      {row.label}
                    </div>
                  </div>
                  <div
                    style={{
                      color: C.text,
                      fontSize: 13,
                      fontWeight: 600,
                      fontVariantNumeric: 'tabular-nums',
                      flexShrink: 0,
                    }}>
                    {number(row.value)}
                  </div>
                </div>
                <div style={{ marginTop: 8, height: 2, background: 'var(--tx-line)', borderRadius: 1, overflow: 'hidden' }}>
                  <div
                    style={{
                      width: `${Math.min(100, (row.value / max) * 100)}%`,
                      height: '100%',
                      background: i === 0 ? C.lime : C.muted,
                      borderRadius: 1,
                    }}
                  />
                </div>
                <div style={{ color: C.subtle, fontSize: 11, fontWeight: 500, marginTop: 6 }}>
                  {number(row.secondary ?? 0)} sessions
                </div>
              </div>
            ))}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ padding: 20 }}>
        <SectionHeader title="Visitor geography" meta="U.S. city/state traffic · bubble size = active users" />
        <EmptyState title="No U.S. geography yet" detail="City and state traffic will appear here once GA4 returns U.S. region rows." />
        </div>
      )}
    </Card>
  );
};

const EmptyState: React.FC<{ title: string; detail: string }> = ({ title, detail }) => (
  <div
    style={{
      // a quiet empty state: no dashed outline, just centred copy in the space the content would take
      minHeight: 180,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: 24,
    }}>
    <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{title}</div>
    <div style={{ color: C.muted, fontSize: 12, fontWeight: 500, marginTop: 6, maxWidth: 440, lineHeight: 1.5 }}>
      {detail}
    </div>
  </div>
);

const GaTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color: string;
    payload?: { date?: string };
  }>;
  label?: string;
}> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const hoveredDate = payload[0]?.payload?.date ?? label;

  return (
    <div style={{ ...tooltipStyle, minWidth: 150 }}>
      <div style={{ color: C.muted, fontSize: 11, fontWeight: 500, marginBottom: 8 }}>
        {hoveredDate ? formatFullDate(hoveredDate) : 'Date'}
      </div>
      {payload.map((point) => (
        <div
          key={point.name}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 18,
            alignItems: 'center',
            color: C.text,
            marginTop: 5,
          }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: C.muted }}>
            <Tick color={point.color} />
            {point.name}
          </span>
          <span style={{ color: C.text, fontSize: 13, fontWeight: 600, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
            {number(Number(point.value))}
          </span>
        </div>
      ))}
    </div>
  );
};

const DeviceEngagementTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color: string;
    payload?: { date?: string };
  }>;
  label?: string;
}> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const hoveredDate = payload[0]?.payload?.date ?? label;

  return (
    <div style={{ ...tooltipStyle, minWidth: 170 }}>
      <div style={{ color: C.muted, fontSize: 11, fontWeight: 500, marginBottom: 8 }}>
        {hoveredDate ? formatFullDate(hoveredDate) : 'Date'}
      </div>
      {payload.map((point) => (
        <div
          key={point.name}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 18,
            alignItems: 'center',
            color: C.text,
            marginTop: 5,
          }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: C.muted }}>
            <Tick color={point.color} />
            {point.name}
          </span>
          <span style={{ color: C.text, fontSize: 13, fontWeight: 600, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
            {formatSeconds(Number(point.value))}
          </span>
        </div>
      ))}
    </div>
  );
};

export const AcquisitionsTab = () => {
  const [period, setPeriod] = useState<AdminAcquisitionInsightsPeriod>('last_30');
  const [compare, setCompare] = useState<AdminAcquisitionInsightsCompare>('previous_period');
  const [compareOpen, setCompareOpen] = useState(false);
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const queryParams = useMemo(
    () => ({
      period,
      compare,
      ...(period === 'custom' ? { startDate, endDate } : {}),
    }),
    [compare, endDate, period, startDate],
  );
  const { insights, isLoading, error, isFetching } = useAdminAcquisitionInsights(queryParams);
  const data = insights ?? fallback;
  const ga = {
    ...fallback.ga4,
    ...(data.ga4 ?? {}),
    current: {
      ...fallback.ga4.current,
      ...(data.ga4?.current ?? {}),
    },
    previous: {
      ...fallback.ga4.previous,
      ...(data.ga4?.previous ?? {}),
    },
    deltas: {
      ...fallback.ga4.deltas,
      ...(data.ga4?.deltas ?? {}),
    },
    daily: data.ga4?.daily ?? [],
    deviceEngagement: data.ga4?.deviceEngagement ?? [],
    topSources: data.ga4?.topSources ?? [],
    topPages: data.ga4?.topPages ?? [],
    topEvents: data.ga4?.topEvents ?? [],
    topLocations: data.ga4?.topLocations ?? [],
  };
  const gaUnavailable = !(data.rawAvailability?.ga4 ?? false) || Boolean(error);
  const activePeriod = ga.period ?? fallback.ga4.period;
  const activeCompareLabel = compareLabel(activePeriod.compare);
  const engagementRate = ga.current.sessions ? ga.current.engagedSessions / ga.current.sessions : 0;
  const previousEngagementRate = ga.previous.sessions ? ga.previous.engagedSessions / ga.previous.sessions : 0;
  const viewsPerSession = ga.current.sessions ? ga.current.views / ga.current.sessions : 0;
  const previousViewsPerSession = ga.previous.sessions ? ga.previous.views / ga.previous.sessions : 0;
  const avgEngagementSeconds = ga.current.sessions ? ga.current.engagementSeconds / ga.current.sessions : 0;
  const previousAvgEngagementSeconds = ga.previous.sessions ? ga.previous.engagementSeconds / ga.previous.sessions : 0;
  const trendStats = useMemo(() => {
    const activeDays = ga.daily.filter((day) => day.views > 0 || day.sessions > 0 || day.activeUsers > 0);
    const avgViews = ga.daily.length
      ? Math.round(ga.daily.reduce((sum, day) => sum + day.views, 0) / ga.daily.length)
      : 0;
    const peak = ga.daily.reduce(
      (best, day) => (day.views > best.views ? day : best),
      { date: '', activeUsers: 0, sessions: 0, views: 0 },
    );
    const low = activeDays.reduce(
      (lowest, day) => (day.views < lowest.views ? day : lowest),
      activeDays[0] ?? { date: '', activeUsers: 0, sessions: 0, views: 0 },
    );

    return {
      avgViews,
      peak,
      low,
      activeDays: activeDays.length,
    };
  }, [ga.daily]);

  return (
    <div
      style={{
        fontFamily: font,
        height: '100%',
        overflow: 'auto',
        padding: '20px 0',
        scrollbarWidth: 'none' as const,
        color: C.text,
      }}>
      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, alignItems: 'baseline' }}>
          <SectionTitle>Acquisition command center</SectionTitle>
          <div
            style={{
              color: C.muted,
              fontSize: 11,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}>
            <GaIcon muted={gaUnavailable} />
            {isFetching ? 'Refreshing' : gaUnavailable ? 'GA4 unavailable' : 'GA4 live'}
          </div>
        </div>
        {/* the sources, as legend items: GA4 is the one wired up; the rest are planned */}
        <div style={{ display: 'flex', columnGap: 18, rowGap: 8, flexWrap: 'wrap', marginTop: 14 }}>
          {PLANNED_SOURCES.map((source) => {
            const live = source === 'Google Analytics 4';
            return (
              <span
                key={source}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  fontWeight: 500,
                  color: live ? C.text : C.muted,
                  whiteSpace: 'nowrap',
                }}>
                <Tick color={live && !gaUnavailable ? C.lime : C.monitor} />
                {source}
              </span>
            );
          })}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            marginTop: 20,
          }}>
          <Segmented
            label="Date range"
            value={period}
            onChange={setPeriod}
            options={PERIOD_OPTIONS.map((option): [AdminAcquisitionInsightsPeriod, React.ReactNode] => [
              option.value,
              option.label,
            ])}
          />
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            {period === 'custom' && (
              <>
                <TextInput
                  type="date"
                  aria-label="Start date"
                  value={startDate}
                  inputProps={{ max: endDate }}
                  onChange={(event) => setStartDate(event.target.value)}
                  sx={dateInputSx}
                />
                <TextInput
                  type="date"
                  aria-label="End date"
                  value={endDate}
                  inputProps={{ min: startDate, max: defaultEndDate() }}
                  onChange={(event) => setEndDate(event.target.value)}
                  sx={dateInputSx}
                />
              </>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative' }}>
              <span style={metaStyle}>Compare</span>
              <Button
                aria-haspopup="listbox"
                aria-expanded={compareOpen}
                onClick={() => setCompareOpen((open) => !open)}
                onBlur={() => window.setTimeout(() => setCompareOpen(false), 120)}
                endIcon={
                  <span
                    style={{
                      display: 'inline-flex',
                      color: C.muted,
                      transform: compareOpen ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.16s ease',
                    }}>
                    <Icon name="chev" size={16} />
                  </span>
                }
                sx={{ minWidth: 190, justifyContent: 'space-between', fontFamily: font }}>
                {compareDisplayLabel(compare)}
              </Button>
              {compareOpen && (
                <div
                  role="listbox"
                  aria-label="Compare to"
                  style={{
                    position: 'absolute',
                    top: 46,
                    right: 0,
                    zIndex: 20,
                    minWidth: 190,
                    padding: 4,
                    borderRadius: 12,
                    background: 'var(--tx-glass-panel)',
                    backgroundImage: 'var(--tx-surface-light)',
                    backdropFilter: 'blur(20px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                    boxShadow: 'var(--tx-shadow)',
                  }}>
                  {COMPARE_OPTIONS.map((option) => {
                    const active = option.value === compare;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        role="option"
                        aria-selected={active}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => {
                          setCompare(option.value);
                          setCompareOpen(false);
                        }}
                        style={{
                          width: '100%',
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 12,
                          background: active ? 'var(--tx-fill)' : 'transparent',
                          border: 'none',
                          borderRadius: 8,
                          color: active ? C.text : C.muted,
                          fontFamily: font,
                          fontSize: 12,
                          fontWeight: 600,
                          padding: '0 10px',
                          textAlign: 'left',
                          cursor: 'pointer',
                        }}>
                        {option.label}
                        {active && <Icon name="check" size={14} />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        {gaUnavailable && (
          <div style={{ color: C.muted, fontSize: 12, lineHeight: 1.55, marginTop: 18 }}>
            {data.errors[0] ??
              (error instanceof Error ? error.message : 'GA4 access is not available yet. Confirm the service account has Viewer access.')}
          </div>
        )}
      </Card>

      <StatRow columns={4}>
        {(
          [
            ['Active users', 'activeUsers'],
            ['New users', 'newUsers'],
            ['Sessions', 'sessions'],
            ['Views', 'views'],
            ['Engaged sessions', 'engagedSessions'],
            ['Events', 'eventCount'],
          ] as const
        ).map(([label, key]) => (
          <MetricCard
            key={key}
            label={label}
            value={ga.current[key]}
            current={ga.current[key]}
            previous={ga.previous[key]}
            delta={ga.deltas[key]}
            comparisonLabel={activeCompareLabel}
            loading={isLoading}
          />
        ))}
        <MetricCard
          label="Engagement rate"
          value={`${Math.round(engagementRate * 100)}%`}
          current={engagementRate}
          previous={previousEngagementRate}
          delta={relativeDelta(engagementRate, previousEngagementRate)}
          comparisonLabel={activeCompareLabel}
          loading={isLoading}
        />
        <MetricCard
          label="Views / session"
          value={decimal(viewsPerSession)}
          current={viewsPerSession}
          previous={previousViewsPerSession}
          delta={relativeDelta(viewsPerSession, previousViewsPerSession)}
          comparisonLabel={activeCompareLabel}
          loading={isLoading}
        />
      </StatRow>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 0.8fr)',
          gap: 20,
          marginBottom: 20,
        }}>
        <Card style={{ minHeight: 650, display: 'flex', flexDirection: 'column' }}>
          <SectionHeader
            title={periodTrendLabel(activePeriod.period)}
            meta={
              <>
                Avg engagement {formatSeconds(avgEngagementSeconds)}
                {previousAvgEngagementSeconds
                  ? ` · ${pct(relativeDelta(avgEngagementSeconds, previousAvgEngagementSeconds))} vs prior`
                  : ''}
              </>
            }
            style={{ marginBottom: 12 }}
          />
          {ga.daily.length ? (
            <div style={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
              <ResponsiveContainer width="100%" height={320}>
                {/* the house time series: 1.5px lines, a faint flat fill under views only, quiet axes, horizontal
                    hairlines; lime is only the hovered point */}
                <AreaChart data={ga.daily} margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
                  <CartesianGrid {...quietGrid} />
                  <XAxis dataKey="date" {...quietAxis} tickFormatter={formatDate} minTickGap={32} tickMargin={8} />
                  <YAxis {...quietAxis} width={36} tickFormatter={compact} allowDecimals={false} />
                  <Tooltip content={<GaTooltip />} cursor={{ stroke: C.border, strokeWidth: 1 }} />
                  <Area type="monotone" dataKey="views" name="Views" {...lineSeries(C.text)} fill={C.text} fillOpacity={0.04} />
                  <Area type="monotone" dataKey="sessions" name="Sessions" {...lineSeries(C.muted)} fill="none" />
                </AreaChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', gap: 18, alignItems: 'center', marginTop: 8 }}>
                {[
                  { label: 'Views', color: C.text },
                  { label: 'Sessions', color: C.muted },
                ].map((item) => (
                  <div key={item.label} style={{ display: 'flex', gap: 7, alignItems: 'center', color: C.muted, fontSize: 11, fontWeight: 500 }}>
                    <Tick color={item.color} />
                    {item.label}
                  </div>
                ))}
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                  gap: 12,
                  borderTop: '1px solid var(--tx-line)',
                  paddingTop: 14,
                  marginTop: 20,
                }}>
                {[
                  { label: 'Avg / day', value: number(trendStats.avgViews) },
                  { label: 'Peak day', value: trendStats.peak.date ? `${number(trendStats.peak.views)} · ${formatDate(trendStats.peak.date)}` : '—' },
                  { label: 'Low day', value: trendStats.low.date ? `${number(trendStats.low.views)} · ${formatDate(trendStats.low.date)}` : '—' },
                  { label: 'Active days', value: `${number(trendStats.activeDays)}/${number(ga.daily.length)}` },
                ].map((item) => (
                  <div key={item.label} style={{ minWidth: 0 }}>
                    <div style={metaStyle}>
                      {item.label}
                    </div>
                    <div
                      style={{
                        color: C.text,
                        fontSize: 13,
                        fontWeight: 600,
                        fontVariantNumeric: 'tabular-nums',
                        marginTop: 5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>
              {ga.deviceEngagement.length > 0 && (
                <div style={{ borderTop: '1px solid var(--tx-line)', marginTop: 'auto', paddingTop: 16 }}>
                  <SectionHeader title="Avg time on site" meta="Desktop vs mobile" style={{ marginBottom: 10 }} />
                  <ResponsiveContainer width="100%" height={130}>
                    <LineChart data={ga.deviceEngagement} margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
                      <Tooltip content={<DeviceEngagementTooltip />} cursor={{ stroke: C.border, strokeWidth: 1 }} />
                      <CartesianGrid {...quietGrid} />
                      <YAxis hide />
                      <Line type="monotone" dataKey="desktop" name="Desktop" {...lineSeries(C.text)} />
                      <Line type="monotone" dataKey="mobile" name="Mobile" {...lineSeries(C.muted)} />
                    </LineChart>
                  </ResponsiveContainer>
                  <div style={{ display: 'flex', gap: 18, alignItems: 'center', marginTop: 8 }}>
                    {[
                      { label: 'Desktop', color: C.text },
                      { label: 'Mobile', color: C.muted },
                    ].map((item) => (
                      <div key={item.label} style={{ display: 'flex', gap: 7, alignItems: 'center', color: C.muted, fontSize: 11, fontWeight: 500 }}>
                        <Tick color={item.color} />
                        {item.label}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <EmptyState title="No GA4 trend yet" detail="Traffic trend will appear once the GA4 Data API returns rows." />
          )}
        </Card>

        <Card>
          <SectionHeader title="Top sources" style={{ marginBottom: 6 }} />
          {ga.topSources.length ? (
            <RankedRows rows={ga.topSources} valueLabel="sessions" secondaryLabel="active users" />
          ) : (
            <EmptyState title="No sources yet" detail="Session source / medium rankings will appear here." />
          )}
        </Card>
      </div>

      <div style={{ marginBottom: 20 }}>
        <GeoMapCard rows={ga.topLocations} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 20 }}>
        <Card>
          <SectionHeader title="Top pages" style={{ marginBottom: 6 }} />
          {ga.topPages.length ? (
            <RankedRows rows={ga.topPages} valueLabel="views" secondaryLabel="active users" />
          ) : (
            <EmptyState title="No page data yet" detail="Top viewed pages and app surfaces will appear here." />
          )}
        </Card>

        <Card>
          <SectionHeader title="Top events" style={{ marginBottom: 6 }} />
          {ga.topEvents.length ? (
            <RankedRows rows={ga.topEvents} valueLabel="events" secondaryLabel="active users" />
          ) : (
            <EmptyState title="No event data yet" detail="GA4 event rankings will appear here once events are available." />
          )}
        </Card>
      </div>
    </div>
  );
};
